// What a note section holds and how a new value is put into it, for every
// section type the EMR scan returns: free text, a choice, a set of choices, or a
// Flex tree. Legacy Flex, flat/list Flex, and Flex++ all write through the
// same answerQuestionInEMR call; the only difference is the answer's path
// shape. Pure functions: no EMR, no React.

import { sameText } from './textDiff';

export const TEXT_TYPES = new Set(['Short Answer', 'Long Answer', 'Date', 'Integer', 'String']);

const MAX_CHOICES_SHOWN = 60;
const FLEX_CONTROL_KEYS = new Set(['flex++', 'prune_early']);

const asText = (value) => (value == null ? '' : String(value));
const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const contentKeys = (value) => Object.keys(value || {}).filter((key) => !FLEX_CONTROL_KEYS.has(key) && !key.startsWith('_'));

const unwrapExistingValue = (leaf) => {
  let value = leaf?.['Existing Value'];
  while (isObject(value) && Object.prototype.hasOwnProperty.call(value, 'Existing Value')) {
    value = value['Existing Value'];
  }
  return value ?? '';
};

function leafChoices(leaf) {
  if (!leaf || typeof leaf !== 'object') return null;
  const choices = [leaf.Choices, leaf.Options, leaf.choices, leaf._choices].find(Array.isArray);
  return choices ? choices.slice() : null;
}

// A field is an object of plain values ('Existing Value', choices, ...). The
// scan crosses extension messaging, which drops each field's Input function, so
// an empty field arrives as {}.
const isLeaf = (value) => isObject(value) &&
  Object.values(value).every((inner) => inner === null || typeof inner !== 'object' || Array.isArray(inner));

const isFlexPlusLeaf = (value) => isObject(value) && contentKeys(value).length === 0;

export function isFlexPlusPlus(structure) {
  if (!structure || typeof structure !== 'object') return false;
  if (!Array.isArray(structure) && structure['flex++'] === true) return true;
  return Object.entries(structure).some(([key, value]) => !FLEX_CONTROL_KEYS.has(key) && isFlexPlusPlus(value));
}

/**
 * Every fillable Flex leaf with its exact answer path. Paths cover the shapes
 * returned across injectors: flat fields, legacy group/field trees, row lists,
 * mixed parallel sections, and arbitrary Flex++ nesting.
 */
export function flexFieldEntries(structure, currentValues) {
  const entries = [];
  const walk = (node, path, flexPlus, container = false) => {
    if (Array.isArray(node)) {
      node.forEach((row, index) => walk(row, [...path, index], flexPlus, true));
      return;
    }
    if (!isObject(node)) {
      if (path.length) entries.push({ path, value: node ?? '', representation: flexPlus ? 'flex++' : 'flex' });
      return;
    }

    const hereIsFlexPlus = flexPlus || node['flex++'] === true;
    if (hereIsFlexPlus && !container && isFlexPlusLeaf(node)) {
      const current = flexValueAtPath(currentValues, path);
      const entry = {
        path,
        value: current !== undefined ? current : node._existingValue ?? '',
        ...(leafChoices(node) && { choices: leafChoices(node) }),
        ...(node._multiple === true && { multiple: true }),
        ...(node._type && { valueType: node._type }),
        representation: 'flex++',
      };
      entries.push(entry);
      return;
    }
    if (!hereIsFlexPlus && !container && isLeaf(node)) {
      const choices = leafChoices(node);
      const current = flexValueAtPath(currentValues, path);
      entries.push({
        path,
        value: current !== undefined ? current : unwrapExistingValue(node),
        ...(choices && { choices }),
        representation: 'flex',
      });
      return;
    }

    contentKeys(node).forEach((key) => walk(node[key], [...path, key], hereIsFlexPlus, false));
  };

  walk(structure, [], Boolean(structure?.['flex++']), true);
  return entries;
}

/**
 * A Flex section's fields as { group: { field: { value, choices? } } }.
 * Groups published as rows (parallel subsections) appear as { rows: n }: they
 * are read-only here.
 */
export function flexFields(structure) {
  const groups = {};
  Object.entries(structure || {}).forEach(([group, fields]) => {
    if (FLEX_CONTROL_KEYS.has(group) || group.startsWith('_')) return;
    if (Array.isArray(fields)) {
      groups[group] = { rows: fields.length };
      return;
    }
    if (!fields || typeof fields !== 'object') return;
    groups[group] = {};
    Object.entries(fields).forEach(([field, leaf]) => {
      if (!isLeaf(leaf)) return;
      const entry = { value: asText(unwrapExistingValue(leaf)) };
      const choices = leafChoices(leaf);
      if (choices) entry.choices = choices.slice(0, MAX_CHOICES_SHOWN).map(asText);
      groups[group][field] = entry;
    });
  });
  return groups;
}

function matchChoice(value, choices) {
  const wanted = asText(value).trim().toLowerCase();
  return choices.find((choice) => asText(choice).trim().toLowerCase() === wanted);
}

const valueIsEmpty = (value) =>
  value == null ||
  (typeof value === 'string' && !value.trim()) ||
  (Array.isArray(value) && value.length === 0);

const sameValue = (left, right) =>
  typeof left === 'string' && typeof right === 'string'
    ? sameText(left, right)
    : JSON.stringify(left) === JSON.stringify(right);

const pathKey = (path) => JSON.stringify(path.map((part) => String(part)));
const pathLabel = (path) => path.join(' › ');

function coerceFlexValue(field, value) {
  if (field.multiple) {
    if (!Array.isArray(value) || value.length === 0) return { error: `Pick at least one of: ${(field.choices || []).join(', ')}` };
    const picked = [];
    for (const item of value) {
      const choice = matchChoice(item, field.choices || []);
      if (choice === undefined) return { error: `"${item}" is not an option. Options: ${(field.choices || []).join(', ')}` };
      if (!picked.includes(choice)) picked.push(choice);
    }
    return { value: picked };
  }
  if (field.choices) {
    const choice = matchChoice(value, field.choices);
    if (choice === undefined) return { error: `"${value}" is not an option. Options: ${field.choices.join(', ')}` };
    return { value: choice };
  }
  if (field.valueType === 'integer' || field.valueType === 'int') {
    const number = Number(value);
    if (!Number.isInteger(number)) return { error: `"${value}" must be an integer` };
    return { value: number };
  }
  if (field.valueType === 'number' || field.valueType === 'float') {
    const number = Number(value);
    if (!Number.isFinite(number)) return { error: `"${value}" must be a number` };
    return { value: number };
  }
  return { value: typeof value === 'string' ? value : asText(value) };
}

export function flexValueAtPath(value, path) {
  return (path || []).reduce((node, part) => (node == null ? undefined : node[part]), value);
}

export function flexAnswerFromEntries(entries, baseEntries = entries) {
  if (!entries?.length) return {};

  // Row-list fillers usually identify an existing row by its first field.
  // Include that identity with a changed cell, but never replay sibling cells
  // that the clinician did not approve (and may have edited meanwhile).
  const samePrefix = (path, prefix) => prefix.every((part, index) => part === path[index]);
  const expanded = new Map();
  entries.forEach(({ path }) => {
    path.forEach((part, index) => {
      if (typeof part !== 'number') return;
      const rowPrefix = path.slice(0, index + 1);
      const identity = (baseEntries || []).find((entry) =>
        entry.path.length > rowPrefix.length && samePrefix(entry.path, rowPrefix),
      );
      if (identity) expanded.set(pathKey(identity.path), identity);
    });
  });
  entries.forEach((entry) => expanded.set(pathKey(entry.path), entry));
  const answerEntries = [...expanded.values()];

  const answer = typeof answerEntries[0].path[0] === 'number' ? [] : {};
  answerEntries.forEach(({ path, value }) => {
    if (!Array.isArray(path) || path.length === 0) return;
    let node = answer;
    path.forEach((part, index) => {
      if (index === path.length - 1) {
        node[part] = value;
        return;
      }
      if (node[part] == null) node[part] = typeof path[index + 1] === 'number' ? [] : {};
      node = node[part];
    });
  });
  return answer;
}

function planPathWrites(structure, updates, currentValues) {
  if (!Array.isArray(updates) || updates.length === 0) {
    return { error: 'field_updates must list at least one { path, value }.' };
  }
  const known = flexFieldEntries(structure, currentValues);
  const byPath = new Map(known.map((field) => [pathKey(field.path), field]));
  const changes = [];
  const seen = new Set();
  for (const update of updates) {
    if (!Array.isArray(update?.path) || update.path.length === 0) return { error: 'Every field update needs a non-empty path.' };
    const key = pathKey(update.path);
    const field = byPath.get(key);
    if (!field) return { error: `No field "${pathLabel(update.path)}" in this section.` };
    const rowIdentity = field.path
      .map((part, index) => typeof part === 'number' ? field.path.slice(0, index + 1) : null)
      .filter(Boolean)
      .map((rowPrefix) => known.find((candidate) =>
        candidate.path.length > rowPrefix.length &&
        rowPrefix.every((part, index) => part === candidate.path[index]),
      ))
      .find((identity) => identity && pathKey(identity.path) === key);
    if (rowIdentity) {
      return {
        error: `Cannot directly rename row identity "${pathLabel(field.path)}"; use comprehend_note so the EMR can safely match the row.`,
      };
    }
    if (seen.has(key)) return { error: `Field "${pathLabel(field.path)}" was listed more than once.` };
    seen.add(key);
    if (valueIsEmpty(update.value)) return { error: `Empty value for ${pathLabel(field.path)}; the EMR ignores empty writes.` };
    const coerced = coerceFlexValue(field, update.value);
    if (coerced.error) return { error: `${pathLabel(field.path)}: ${coerced.error}` };
    if (valueIsEmpty(coerced.value)) return { error: `Empty value for ${pathLabel(field.path)}; the EMR ignores empty writes.` };
    if (!sameValue(coerced.value, field.value)) changes.push({ path: field.path, before: field.value, after: coerced.value });
  }
  if (changes.length === 0) return { error: 'No change: those fields already say that. Nothing to write.' };
  return {
    answer: flexAnswerFromEntries(changes.map(({ path, after }) => ({ path, value: after })), known),
    changes,
  };
}

/**
 * The partial Flex answer for the fields being changed, checked against the
 * section's own fields. Returns { answer, changes } or { error }.
 */
export function planFlexWrite(structure, requested, fieldUpdates, currentValues) {
  if (fieldUpdates !== undefined) return planPathWrites(structure, fieldUpdates, currentValues);
  if (isFlexPlusPlus(structure)) {
    return { error: 'Flex++ fields must be changed with field_updates from get_note_sections.' };
  }
  if (!requested || typeof requested !== 'object' || Array.isArray(requested)) {
    return { error: 'fields must be an object: { group: { field: value } }' };
  }
  const known = flexFields(structure);
  const answer = {};
  const changes = [];
  for (const [group, fields] of Object.entries(requested)) {
    const knownGroup = known[group];
    if (!knownGroup) return { error: `No group "${group}" in this section. Groups: ${Object.keys(known).join(', ')}` };
    if ('rows' in knownGroup) return { error: `"${group}" is a table of rows; re-run it with comprehend_note.` };
    if (!fields || typeof fields !== 'object' || Array.isArray(fields)) {
      return { error: `fields["${group}"] must be an object of field: value` };
    }
    for (const [field, value] of Object.entries(fields)) {
      const knownField = knownGroup[field];
      if (!knownField) {
        return { error: `No field "${field}" in "${group}". Fields: ${Object.keys(knownGroup).join(', ')}` };
      }
      let next = asText(value);
      if (knownField.choices) {
        const choice = matchChoice(next, knownField.choices);
        if (choice === undefined) {
          return { error: `"${next}" is not an option for ${group} › ${field}. Options: ${knownField.choices.join(', ')}` };
        }
        next = choice;
      }
      if (!next.trim()) return { error: `Empty value for ${group} › ${field}; the EMR ignores empty writes.` };
      // Rewriting a field with the same words is not a change; leave it out
      if (sameText(next, knownField.value)) continue;
      answer[group] = { ...(answer[group] || {}), [field]: next };
      changes.push({ group, field, before: knownField.value, after: next });
    }
  }
  if (changes.length === 0) return { error: 'No change: those fields already say that. Nothing to write.' };
  return { answer, changes };
}

/** A Multiple Choice / Choose All answer, checked against the section's choices. */
export function planChoiceWrite(section, { choice, choices }) {
  const options = (section.Choices || []).map(asText);
  if (section.Type === 'Multiple Choice') {
    const picked = matchChoice(choice, options);
    if (picked === undefined) return { error: `Pick one of: ${options.join(', ')}` };
    return { answer: picked, after: picked };
  }
  const wanted = Array.isArray(choices) ? choices : choice !== undefined ? [choice] : [];
  const picked = [];
  for (const value of wanted) {
    const match = matchChoice(value, options);
    if (match === undefined) return { error: `"${value}" is not an option. Options: ${options.join(', ')}` };
    if (!picked.includes(match)) picked.push(match);
  }
  if (picked.length === 0) return { error: `Pick at least one of: ${options.join(', ')}` };
  return { answer: picked, after: picked.join(', ') };
}

// ---- Template instructions --------------------------------------------------
// A Flex section's instruction is stored as JSON:
// { TopLevelInstruction, SubSections: { name: { Instruction, replyType } } }.

export function parseFlexInstruction(raw) {
  if (typeof raw !== 'string' || !raw.trim().startsWith('{')) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && ('TopLevelInstruction' in parsed || 'SubSections' in parsed) ? parsed : null;
  } catch (err) {
    return null;
  }
}

/** How get_template shows one section's instruction. */
export function describeInstruction(raw, limit) {
  const clip = (text) => (text.length > limit ? `${text.slice(0, limit)}…` : text);
  const flex = parseFlexInstruction(raw);
  if (!flex) return { instruction: clip(asText(raw)) };
  return {
    instruction: clip(asText(flex.TopLevelInstruction)),
    subsections: Object.entries(flex.SubSections || {}).map(([name, sub]) => ({
      name,
      instruction: clip(asText(sub?.Instruction)),
      replyType: sub?.replyType || 'overwrite',
    })),
  };
}

/**
 * A Flex section's View More subsections, as the panel finds them in the EMR
 * scan (ConversationDetailsCX addFlexSubsections): its groups, or the fields
 * of a lone group. They have no saved instruction until one is written.
 */
export function viewMoreNames(structure) {
  const keys = contentKeys(structure);
  if (keys.length === 1) {
    const only = structure[keys[0]];
    if (only && typeof only === 'object' && !Array.isArray(only) && !isLeaf(only)) return contentKeys(only);
  }
  return keys.filter((key) => structure[key] && typeof structure[key] === 'object' && Object.keys(structure[key]).length > 0);
}

/**
 * The stored instruction after one edit: the whole text, the Flex top-level
 * instruction, or one subsection's instruction / reply type. A View More
 * subsection (in `viewMore`, from the scan) that has no saved instruction yet
 * is added, the way the panel saves one: { TopLevelInstruction, SubSections }.
 * Returns { raw, before, after } or { error }.
 */
export function editInstruction(raw, { subsection, instruction, replyType, viewMore = [] }) {
  let flex = parseFlexInstruction(raw);
  if (!flex) {
    if (!subsection) return { raw: asText(instruction), before: asText(raw), after: asText(instruction) };
    if (!viewMore.includes(subsection)) {
      return { error: `No subsection "${subsection}".${viewMore.length ? ` View More subsections: ${viewMore.join(', ')}` : ' This section has no subsections.'}` };
    }
    flex = { TopLevelInstruction: asText(raw), SubSections: {} };
  }
  const next = JSON.parse(JSON.stringify(flex));
  next.SubSections = next.SubSections || {};
  if (!subsection) {
    next.TopLevelInstruction = asText(instruction);
    return { raw: JSON.stringify(next), before: asText(flex.TopLevelInstruction), after: next.TopLevelInstruction };
  }
  let current = flex.SubSections?.[subsection];
  if (!current && viewMore.includes(subsection)) current = { Instruction: '', replyType: 'overwrite' };
  if (!current) {
    const known = [...new Set([...Object.keys(flex.SubSections || {}), ...viewMore])];
    return { error: `No subsection "${subsection}". Subsections: ${known.join(', ')}` };
  }
  if (replyType !== undefined) {
    next.SubSections[subsection] = { ...current, replyType };
    return { raw: JSON.stringify(next), before: current.replyType || 'overwrite', after: replyType };
  }
  next.SubSections[subsection] = { ...current, Instruction: asText(instruction) };
  return { raw: JSON.stringify(next), before: asText(current.Instruction), after: asText(instruction) };
}

// ---- Undo -------------------------------------------------------------------

/**
 * A Flex section's current values as an answer, in the exact shape the
 * injector writes back. Control/schema keys are never included.
 */
export function flexAnswerFromStructure(structure, currentValues) {
  return flexAnswerFromEntries(flexFieldEntries(structure, currentValues));
}

/** One instruction as readable text (a Flex one as its top level plus subsections), for diffs. */
export function instructionText(raw) {
  const flex = parseFlexInstruction(raw);
  if (!flex) return asText(raw);
  const subsections = Object.entries(flex.SubSections || {}).map(
    ([name, sub]) => `${name} (${sub?.replyType || 'overwrite'}): ${asText(sub?.Instruction)}`,
  );
  return [asText(flex.TopLevelInstruction), ...subsections].filter(Boolean).join('\n\n');
}

/**
 * `current` with only the named subsections put back as they were in `old`
 * (a subsection that didn't exist before is removed again). For undoing part
 * of an instruction change.
 */
export function restoreSubsections(currentRaw, oldRaw, names) {
  const current = parseFlexInstruction(currentRaw) || { TopLevelInstruction: asText(currentRaw), SubSections: {} };
  const old = parseFlexInstruction(oldRaw) || { TopLevelInstruction: asText(oldRaw), SubSections: {} };
  const next = JSON.parse(JSON.stringify(current));
  next.SubSections = next.SubSections || {};
  names.forEach((name) => {
    if (old.SubSections?.[name]) next.SubSections[name] = old.SubSections[name];
    else delete next.SubSections[name];
  });
  return JSON.stringify(next);
}
